import { useMemo, useState } from 'react';
import { Check, X, RotateCcw, AlertTriangle, DollarSign } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog.jsx';
import { DatePicker } from '@/components/ui/date-picker.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { useReferrals } from '@/hooks/useReferrals.js';
import {
  qualifyReferral, disqualifyReferral, reopenReferral, payQuarter, reopenQuarter,
} from '@/services/referrals.service.js';

// Referral-partner admin. Restores the v1 partner-portal broker workflow
// inside TRACON V2: see every partner referral, decide qualified/disqualified
// with the three auto-checks as context, and settle a closed quarter's payout.
// Backend: routes/tracon.routes.js "Referrals" block (broker JWT).

const STATUS_META = {
  pending:        { label: 'Pending',      variant: 'warning' },
  qualified:      { label: 'Qualified',    variant: 'info' },
  pending_payout: { label: 'Owed',         variant: 'accent' },
  paid:           { label: 'Paid',         variant: 'success' },
  disqualified:   { label: 'Disqualified', variant: 'muted' },
};

const STATUS_FILTERS = [
  { key: 'all',            label: 'All' },
  { key: 'pending',        label: 'Pending' },
  { key: 'qualified',      label: 'Qualified' },
  { key: 'pending_payout', label: 'Owed' },
  { key: 'paid',           label: 'Paid' },
  { key: 'disqualified',   label: 'Disqualified' },
];

function fmtUSD(n) {
  return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function ReferralsAdmin() {
  // Default straight to CAN — the only partner with a live agreement — so the
  // first (auto-check-heavy) fetch is already scoped. The response's `partners`
  // array still lists every active partner for the tabs, so if CAN ever goes
  // away the user just clicks another tab.
  const [slug, setSlug] = useState('CAN');
  const [statusFilter, setStatusFilter] = useState('all');
  const [busy, setBusy] = useState(false);
  const [reasonDlg, setReasonDlg] = useState(null); // { action, ref }
  const [payDlg, setPayDlg] = useState(null);        // { quarter, count, amount }

  const { partners, rows, counts, staleAfterDays, error, loading, refresh } = useReferrals(slug);

  const partner = useMemo(
    () => partners.find((p) => p.slug === slug) || null,
    [partners, slug]
  );
  const fee = Number(partner?.fee_per_qualified_app || 0);

  const visibleRows = useMemo(() => {
    if (statusFilter === 'all') return rows;
    return rows.filter((r) => (r.qualification_status || 'pending') === statusFilter);
  }, [rows, statusFilter]);

  // Closed-quarter payout rollup for the selected partner.
  const payoutQuarters = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (!r.quarter_closed) continue;
      const q = r.quarter;
      if (!map.has(q)) map.set(q, { quarter: q, owedCount: 0, paidCount: 0, lastPaidAt: null });
      const g = map.get(q);
      const s = r.qualification_status;
      if (s === 'qualified' || s === 'pending_payout') g.owedCount++;
      else if (s === 'paid') {
        g.paidCount++;
        if (r.payout_paid_at && (!g.lastPaidAt || r.payout_paid_at > g.lastPaidAt)) g.lastPaidAt = r.payout_paid_at;
      }
    }
    return [...map.values()]
      .filter((g) => g.owedCount > 0 || g.paidCount > 0)
      .sort((a, b) => (a.quarter < b.quarter ? 1 : -1));
  }, [rows]);

  async function runAction(fn, successMsg) {
    setBusy(true);
    try {
      const res = await fn();
      toast.success(typeof successMsg === 'function' ? successMsg(res) : successMsg);
      await refresh();
      return res;
    } catch (err) {
      toast.error(err?.body?.error || err?.code || 'Action failed');
      throw err;
    } finally {
      setBusy(false);
    }
  }

  function onReasonSubmit(reason) {
    const { action, ref } = reasonDlg;
    setReasonDlg(null);
    if (action === 'qualify') {
      runAction(() => qualifyReferral(ref.partner_ref_id, reason), `${ref.partner_ref_id} qualified`).catch(() => {});
    } else {
      runAction(() => disqualifyReferral(ref.partner_ref_id, reason), `${ref.partner_ref_id} disqualified`).catch(() => {});
    }
  }

  function onPayConfirm(paidAt) {
    const { quarter } = payDlg;
    setPayDlg(null);
    runAction(
      () => payQuarter(slug, quarter, paidAt),
      (res) => `Recorded ${fmtUSD(res.total_amount)} paid to ${partner?.display_name || slug} for ${quarter}`
    ).catch(() => {});
  }

  return (
    <div className="p-4">
      <div>
        <h2 className="text-xl font-semibold text-text">Referrals</h2>
        <p className="mt-1 text-sm text-text-muted">
          Partner referrals · qualify, disqualify &amp; settle quarterly payouts
        </p>
      </div>

      {/* Partner tabs */}
      {partners.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {partners.map((p) => (
            <button
              key={p.slug}
              type="button"
              onClick={() => { setSlug(p.slug); setStatusFilter('all'); }}
              className={[
                'rounded-pill border px-3 py-1 text-sm',
                p.slug === slug
                  ? 'border-accent/50 bg-accent-soft text-accent'
                  : 'border-border text-text-muted hover:text-text',
              ].join(' ')}
            >
              {p.display_name || p.slug}
              <span className="ml-1.5 font-mono text-xs text-text-dim">{fmtUSD(p.fee_per_qualified_app)}/app</span>
            </button>
          ))}
        </div>
      )}

      {/* Payout cards for closed quarters */}
      {partner && payoutQuarters.length > 0 && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {payoutQuarters.map((g) => (
            <div key={g.quarter} className="rounded-card border border-border bg-surface p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-text">{g.quarter}</span>
                {g.owedCount > 0 ? (
                  <Badge variant="accent">{fmtUSD(g.owedCount * fee)} owed</Badge>
                ) : (
                  <Badge variant="success">Settled</Badge>
                )}
              </div>
              <div className="mt-1 text-xs text-text-muted">
                {g.owedCount > 0 && <span>{g.owedCount} app{g.owedCount === 1 ? '' : 's'} to pay. </span>}
                {g.paidCount > 0 && (
                  <span>
                    {fmtUSD(g.paidCount * fee)} paid{g.lastPaidAt ? ` ${g.lastPaidAt}` : ''}.
                  </span>
                )}
              </div>
              <div className="mt-2 flex gap-2">
                {g.owedCount > 0 && (
                  <Button
                    size="sm"
                    variant="accent"
                    disabled={busy}
                    onClick={() => setPayDlg({ quarter: g.quarter, count: g.owedCount, amount: g.owedCount * fee })}
                  >
                    <DollarSign size={14} strokeWidth={2} /> Record payment
                  </Button>
                )}
                {g.paidCount > 0 && g.owedCount === 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() =>
                      runAction(() => reopenQuarter(slug, g.quarter), `${g.quarter} reopened`).catch(() => {})
                    }
                  >
                    <RotateCcw size={14} strokeWidth={1.5} /> Reopen
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Status filter pills */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => {
          const n = f.key === 'all'
            ? (counts ? Object.entries(counts).filter(([k]) => k !== 'stale_pending').reduce((a, [, v]) => a + v, 0) : null)
            : (counts ? counts[f.key] : null);
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={[
                'rounded-pill border px-2.5 py-1 text-xs',
                statusFilter === f.key
                  ? 'border-accent/50 bg-accent-soft text-accent'
                  : 'border-border text-text-dim hover:text-text',
              ].join(' ')}
            >
              {f.label}
              {n != null && <span className="ml-1 font-mono">{n}</span>}
            </button>
          );
        })}
        {counts?.stale_pending > 0 && (
          <span className="inline-flex items-center gap-1 rounded-pill bg-warning-soft px-2.5 py-1 text-xs text-warning">
            <AlertTriangle size={12} strokeWidth={2} /> {counts.stale_pending} stale (&gt;{staleAfterDays}d)
          </span>
        )}
      </div>

      {/* List */}
      <div className="mt-4">
        {loading && !rows.length && (
          <div className="rounded-card border border-border bg-surface p-6 text-sm text-text-muted">Loading referrals…</div>
        )}
        {error && !rows.length && (
          <div className="rounded-card border border-accent/40 bg-accent-soft p-4 text-sm text-accent">
            Couldn't load referrals: {error.body?.error || error.code || error.message}
          </div>
        )}
        {!loading && !error && visibleRows.length === 0 && (
          <div className="rounded-card border border-dashed border-border p-6 text-center text-sm text-text-muted">
            No {statusFilter === 'all' ? '' : `${statusFilter} `}referrals for {partner?.display_name || slug || 'this partner'}.
          </div>
        )}
        {visibleRows.length > 0 && (
          <div className="overflow-x-auto rounded-card border border-border bg-surface">
            <ReferralTable
              rows={visibleRows}
              busy={busy}
              onQualify={(ref) => setReasonDlg({ action: 'qualify', ref })}
              onDisqualify={(ref) => setReasonDlg({ action: 'disqualify', ref })}
              onReopen={(ref) => runAction(() => reopenReferral(ref.partner_ref_id), `${ref.partner_ref_id} reopened`).catch(() => {})}
            />
          </div>
        )}
      </div>

      <ReasonDialog dlg={reasonDlg} busy={busy} onClose={() => setReasonDlg(null)} onSubmit={onReasonSubmit} />
      <PayDialog
        dlg={payDlg}
        partnerName={partner?.display_name || slug}
        onClose={() => setPayDlg(null)}
        onConfirm={onPayConfirm}
      />
    </div>
  );
}

function CheckDot({ ok, label, detail }) {
  return (
    <span
      title={detail}
      className={[
        'inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium',
        ok ? 'bg-success-soft text-success' : 'bg-border/40 text-text-dim',
      ].join(' ')}
    >
      {ok ? <Check size={10} strokeWidth={2.5} /> : <X size={10} strokeWidth={2.5} />}
      {label}
    </span>
  );
}

function ReferralTable({ rows, busy, onQualify, onDisqualify, onReopen }) {
  const tmpl = '10rem 7rem 1.4fr 9rem 8rem 11rem';
  return (
    <div className="min-w-[56rem]">
      <div
        className="grid gap-2 border-b border-border bg-surface-strong/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-dim"
        style={{ gridTemplateColumns: tmpl }}
      >
        {['Ref ID', 'Referred', 'Client', 'Checks', 'Status', 'Actions'].map((l) => <span key={l}>{l}</span>)}
      </div>
      {rows.map((r) => {
        const meta = STATUS_META[r.qualification_status] || STATUS_META.pending;
        const ac = r.auto_checks || {};
        return (
          <div
            key={r.session_id}
            className="grid items-center gap-2 border-b border-border px-3 py-2.5 text-sm last:border-0"
            style={{ gridTemplateColumns: tmpl }}
          >
            <span className="truncate font-mono text-xs font-semibold text-text">
              {r.partner_ref_id || '—'}
              {r.stale_pending && <span className="ml-1 text-warning" title="Stale pending">•</span>}
            </span>
            <span className="text-text-muted">
              {(r.created_at || '').slice(0, 10)}
              <span className="ml-1 text-text-dim">{r.quarter}</span>
            </span>
            <span className="truncate text-text">
              {r.contact_name || r.account_name || <span className="text-text-dim">—</span>}
              {r.account_name && r.contact_name && r.account_name !== r.contact_name && (
                <span className="ml-1 text-xs text-text-dim">· {r.account_name}</span>
              )}
            </span>
            <span className="flex flex-wrap gap-1">
              <CheckDot ok={ac.faa_aircraft?.pass} label="FAA" detail={ac.faa_aircraft?.detail} />
              <CheckDot ok={ac.verified_pilot?.pass} label="PHF" detail={ac.verified_pilot?.detail} />
              <CheckDot ok={ac.effective_within_90d?.pass} label="≤90d" detail={ac.effective_within_90d?.detail} />
            </span>
            <span className="min-w-0">
              <Badge variant={meta.variant}>{meta.label}</Badge>
              {r.qualification_reason && (
                <span className="mt-0.5 block truncate text-[11px] text-text-dim" title={r.qualification_reason}>
                  {r.qualification_reason}
                </span>
              )}
            </span>
            <span className="flex flex-wrap gap-1.5">
              {r.qualification_status === 'pending' && (
                <>
                  <Button size="xs" variant="success" disabled={busy} onClick={() => onQualify(r)}>Qualify</Button>
                  <Button size="xs" variant="outline" disabled={busy} onClick={() => onDisqualify(r)}>Disqualify</Button>
                </>
              )}
              {(r.qualification_status === 'qualified'
                || r.qualification_status === 'pending_payout'
                || r.qualification_status === 'disqualified') && (
                <Button size="xs" variant="ghost" disabled={busy} onClick={() => onReopen(r)}>
                  <RotateCcw size={12} strokeWidth={1.5} /> Reopen
                </Button>
              )}
              {r.qualification_status === 'paid' && (
                <span className="text-[11px] text-text-dim">paid {(r.payout_paid_at || '').slice(0, 10)}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ReasonDialog({ dlg, busy, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const open = !!dlg;
  const isDisqualify = dlg?.action === 'disqualify';
  // Reset the field whenever a new dialog opens.
  const key = dlg ? `${dlg.action}:${dlg.ref?.partner_ref_id}` : 'closed';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setReason(''); onClose(); } }}>
      <DialogContent key={key}>
        <DialogHeader>
          <DialogTitle>
            {isDisqualify ? 'Disqualify' : 'Qualify'} {dlg?.ref?.partner_ref_id}
          </DialogTitle>
          <DialogDescription>
            {isDisqualify
              ? 'A reason is required. It is logged for audit and never shared with the partner.'
              : 'Optional note for the audit log. Not shared with the partner.'}
          </DialogDescription>
        </DialogHeader>
        <textarea
          autoFocus
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={isDisqualify ? 'e.g. No application submitted' : 'e.g. Application submitted, within renewal window'}
          className="w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => { setReason(''); onClose(); }}>Cancel</Button>
          <Button
            variant={isDisqualify ? 'outline' : 'success'}
            disabled={busy || (isDisqualify && !reason.trim())}
            onClick={() => { onSubmit(reason); setReason(''); }}
          >
            {isDisqualify ? 'Disqualify' : 'Qualify'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayDialog({ dlg, partnerName, onClose, onConfirm }) {
  const [paidAt, setPaidAt] = useState(todayYmd());
  const open = !!dlg;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setPaidAt(todayYmd()); onClose(); } }}>
      <DialogContent key={dlg?.quarter || 'closed'}>
        <DialogHeader>
          <DialogTitle>Record payout — {dlg?.quarter}</DialogTitle>
          <DialogDescription>
            Marks {dlg?.count} qualified app{dlg?.count === 1 ? '' : 's'} for {partnerName} as paid
            ({fmtUSD(dlg?.amount)}). Record this only after the payment has actually been sent.
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-muted">Payment date</label>
          <DatePicker value={paidAt} onChange={setPaidAt} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setPaidAt(todayYmd()); onClose(); }}>Cancel</Button>
          <Button variant="accent" disabled={!paidAt} onClick={() => { onConfirm(paidAt); setPaidAt(todayYmd()); }}>
            <DollarSign size={14} strokeWidth={2} /> Mark {fmtUSD(dlg?.amount)} paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
